import { Box, FormControl, TextField } from '@mui/material';
import { DataGrid } from 'devextreme-react';
import ReactDOM from "react-dom/client";
import { PricelistApis } from 'hooks/apis/PricelistApis';
import { IClientDiscount } from 'interfaces/Pricelist/IClientDiscount';
import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PageTitle from '../../../components/PageTitle';
import './AllClientPricelists.scss';

// Static — never depend on component state, so hoist entirely outside the component
const scrollingConfig = { mode: "virtual" as const, preloadEnabled: true }
const selectionConfig = { mode: "single" as const };
const groupingConfig = { autoExpandAll: true, allowCollapsing: true };
const headerFilterConfig = { visible: false };
const pagingConfig = { enabled: false };
const groupPanelConfig = { visible: true };

const AllClientPricelists = () => {
  const navigate = useNavigate();
  const pageGrid = useRef(null);
  const { getAllPricelists } = PricelistApis();
  const [pricelists, setPricelists] = useState<IClientDiscount[] | undefined>(null);
  const [textFilter, setTextFilter] = useState<string>('');

  const searchPanelConfig = useMemo(() => ({ visible: false, text: textFilter }), [textFilter]);

  const columns = useMemo(() => [
    { dataField: "Client", dataType: "string" as const },
    { dataField: "DisplayAddress", caption: "Address", dataType: "string" as const },
    { dataField: "ClientMemo", caption: "Memo", dataType: "string" as const },
    {
      dataField: "GeneralDiscount",
      caption: "Discount",
      alignment: "left" as const,
      width: 80,
      cellTemplate: (container: HTMLElement, options: any) => {
        const discount = options?.data?.GeneralDiscount;
        container.textContent = discount < 0
          ? `BP${Math.abs(discount)}`
          : discount === 0
          ? "B0"
          : `BM${discount}`;
      },
    },
    { dataField: "IsProspect", dataType: "boolean" as const, width: 105 },
    { dataField: "IsCodOnly", dataType: "boolean" as const, width: 105 },
    {
      cssClass: "linkCell",
      caption: "",
      width: 100,
      alignment: "center" as const,
      dataType: "string" as const,
      cellTemplate: (container: HTMLElement, options: any) => {
        const encodedClient = encodeURIComponent(options?.data?.Client);
        const url = `/pricelists/${encodedClient}`;
        const linkElement = (
            <a href={url} className="view-link" onClick={(e) => {
          if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
            return;
          }
          e.preventDefault();
          navigate(url);
        }}
    >
            <VisibilityIcon /> <span>View</span>
          </a>
        );
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
        const root = ReactDOM.createRoot(container);
        root.render(linkElement);
      },
    },
  ], [navigate]);

  useEffect(() => {
    const instance = pageGrid.current?.instance;
    if (!instance) return;
    instance.pageIndex(0);
  }, [textFilter]);

  useEffect(() => {
    getAllPricelists().then((data) => {
      if (!data.Message) {
        setPricelists(data);
      } else {
        // Do something with the error message on the page
      }
    });
  }, [getAllPricelists]);

  return (
    <>
      <PageTitle pageTitle="Client Pricelists" />

      <Box>
        <Box className="searchbar">
          <FormControl fullWidth>
            <TextField
              value={textFilter}
              onChange={(item) => setTextFilter(item?.target?.value)}
              size="small"
              label="Filter Client Pricelists"
              fullWidth
            />
          </FormControl>
        </Box>

        <Box className="clientPricelists">
          <DataGrid
            ref={pageGrid}
            height={800}
            dataSource={pricelists}
            allowColumnReordering={true}
            rowAlternationEnabled={true}
            searchPanel={searchPanelConfig}
            showBorders={false}
            wordWrapEnabled={true}
            hoverStateEnabled={false}
            onRowDblClick={(e) =>
              navigate(`/pricelists/${encodeURIComponent(e?.data?.Client)}`)
            }
            paging={pagingConfig}
            groupPanel={groupPanelConfig}
            grouping={groupingConfig}
            scrolling={scrollingConfig}
            headerFilter={headerFilterConfig}
            selection={selectionConfig}
            columns={columns}
          />
        </Box>
      </Box>
    </>
  );
};

export default AllClientPricelists;
